import { useState } from "react";
import useFavoriteStore from "../../Store/FavoriteStore.jsx";
import CustomDatePicker from "../ui/DatePicker.jsx";
import {
  FormModal,
  Form,
  SmartFormField,
  SmartPillGroup,
  SmartControl,
  FormSection,
  FormLabel,
  SubmitButton,
  PillGroup
} from "../ui/Form";
import { eventSchema } from "../../schemas/eventSchema.js";
import { useTranslation } from "react-i18next";

const CATEGORY_LIST = [
  "Birthday",
  "Wedding",
  "Anniversary",
  "Graduation",
  "New Born",
  "Engagement",
  "Holiday",
  "Cultural",
  "Seasonal",
  "Professional",
];

export default function Component(props) {
  const { t } = useTranslation();
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const { handleAddingNewSpaceOrEvent } = useFavoriteStore();

  const translatedCategories = CATEGORY_LIST.map(cat => {
    // Convert "New Born" to "newBorn" for key generation
    const camelCaseCat = cat.charAt(0).toLowerCase() + cat.slice(1).replace(/\s+/g, '');
    const key = `events.${camelCaseCat}Cat`;
    return {
      value: cat,
      label: t(key, { defaultValue: cat })
    };
  });

  const parseTimeString = (timeString) => {
    const [hours, minutes] = timeString.split(":");
    const date = new Date();
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
    date.setSeconds(0);
    return date;
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (data, { setFormError }) => {
    let formDataToSave = { ...data };
    formDataToSave.eventName = formDataToSave.eventName.trimEnd();

    if (data.eventImage) {
      try {
        const base64Image = await convertToBase64(data.eventImage);
        formDataToSave.eventImage = base64Image;
      } catch (error) {
        console.error("Error converting image to base64:", error);
      }
    }

    const response = await handleAddingNewSpaceOrEvent(
      "events",
      formDataToSave,
      []
    );

    if (response.status === 400 || response === 400) {
      throw new Error(
        t("eventsAndSpaces.eventExistsError")
      );
    } else {
      props.setNewEventIsOpen(false);
      props.handleFetchEvents();
      props.setSelectedEvent(data.eventName);
      props.setEventOpen(false);
      props.handleEventClick(
        "events",
        data.eventName,
        data.eventDate
      );
    }
  };

  return (
    <FormModal
      isOpen={true}
      onClose={() => props.setNewEventIsOpen(false)}
      title={t("eventsAndSpaces.createNewEvent")}
      maxWidth="max-w-xl"
      className="bg-gradient-to-b from-red-50 to-grey-500"
      titleClassName="text-start text-3xl text-neutral-green-500"
    >
      <Form
        schema={eventSchema}
        onSubmit={handleSubmit}
        defaultValues={{
          eventName: "",
          eventDate: null,
          eventTime: "00:00",
          eventLocation: "",
          eventDescription: "",
          eventImage: null,
          category: "",
        }}
        className="space-y-6"
      >
        <div className="flex flex-col md:flex-row gap-6">
          <SmartFormField
            name="eventName"
            label={t("eventsAndSpaces.name")}
            required
            className="flex-1"
            inputProps={{
              placeholder: t("eventsAndSpaces.enterEventName")
            }}
          />

          <SmartControl
            name="eventDate"
            label={t("eventsAndSpaces.date")}
            render={({ value, onChange, error }) => (
              <div className="flex flex-col gap-1 flex-1">
                <FormLabel required>{t("eventsAndSpaces.date")}</FormLabel>
                <CustomDatePicker
                  selected={value ? new Date(value) : null}
                  onChange={(date) =>
                    onChange(date ? date.toISOString().substring(0, 10) : null)
                  }
                  minDate={new Date()}
                  className="block w-full px-4 py-2 border rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  dateFormat="yyyy-MM-dd"
                  placeholderText={t("eventsAndSpaces.selectDate")}
                  showPopperArrow={false}
                />
                {error && <span className="text-red-500 text-xs">{t(error, { defaultValue: error })}</span>}
              </div>
            )}
          />
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          <SmartControl
            name="eventTime"
            label={t("eventsAndSpaces.time")}
            render={({ value, onChange, error }) => (
              <div className="flex flex-col gap-1 flex-1">
                <FormLabel required>{t("eventsAndSpaces.time")}</FormLabel>
                <CustomDatePicker
                  selected={value ? parseTimeString(value) : null}
                  onChange={(date) => {
                    if (!date) {
                      onChange(null);
                      return;
                    }
                    const formattedTime = date.toLocaleTimeString("en-GB", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    });
                    onChange(formattedTime);
                  }}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  dateFormat="h:mm aa"
                  placeholderText={t("eventsAndSpaces.selectTime")}
                  showPopperArrow={false}
                  className="block w-full px-4 py-2 border rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                />
                {error && <span className="text-red-500 text-xs">{t(error, { defaultValue: error })}</span>}
              </div>
            )}
          />
        </div>

        <FormSection
          title={showOptionalFields ? t("eventsAndSpaces.hideOptionalFields") : t("eventsAndSpaces.showOptionalFields")}
          collapsible
          isOpen={showOptionalFields}
          onToggle={setShowOptionalFields}
        >
          <div className="flex flex-col gap-6 px-2 py-2">
            <SmartFormField
              name="eventLocation"
              label={t("eventsAndSpaces.location")}
              inputProps={{
                placeholder: t("eventsAndSpaces.enterEventLocation")
              }}
            />

            <SmartFormField
              name="eventDescription"
              label={t("gift.description")}
              type="textarea"
              inputProps={{
                rows: 4,
                placeholder: t("eventsAndSpaces.eventDescPlaceholder")
              }}
            />
          </div>
        </FormSection>

        <div className="space-y-2">
          <FormLabel required>{t("eventsAndSpaces.eventType")}</FormLabel>
          <SmartPillGroup
            name="category"
            label={t("eventsAndSpaces.eventType")}
            options={translatedCategories}
            size="xs2"
          />
        </div>

        <SubmitButton
          variant="primary"
          size="lg"
          rounded="full"
          fullWidth
          className="mt-5"
        >
          {t("eventsAndSpaces.addEvent")}
        </SubmitButton>
      </Form>
    </FormModal>
  );
}
